import apiClient from './apiClient'

export type HomeworkTocItem = { id: string; level: number; text: string }

export type HomeworkAnchorJson =
  | { quote: string; prefix?: string; suffix?: string }
  | { imageSrc: string; xPct: number; yPct: number; wPct: number; hPct: number }

export type HomeworkComment = {
  id: number
  submissionId: number
  authorUserId?: number | null
  authorName?: string | null
  parentId?: number | null
  source: 'admin' | 'student' | 'ai'
  body: string
  anchorType?: 'text' | 'image' | null
  anchorJson?: HomeworkAnchorJson | string | null
  status: 'open' | 'resolved'
  visibility: 'visible' | 'pending_approval' | 'rejected'
  resolvedBy?: number | null
  resolvedByName?: string | null
  resolvedAt?: string | null
  createdAt?: string
}

export type CourseHomework = {
  id: number
  courseId: number
  name: string
  contentHtml: string
  templateHtml?: string | null
  homeworkType?: 'submission' | 'self_practice'
  allowCompletionNote?: number | boolean
  isActive: number | boolean
  sort: number
  aiAutoScanEnabled: number | boolean
  aiRequireAdminApprove: number | boolean
  aiCanMarkComplete: number | boolean
  aiScanCooldownMinutes: number
  classroomCount?: number
  submissionCount?: number
  classrooms?: Array<{
    id: number
    classroomId: number
    classroomName?: string
    classroomCode?: string
    deadline?: string | null
  }>
  skillLinks?: any[]
}

export type HomeworkSubmission = {
  id: number
  homeworkId: number
  userId: number
  classroomId: number
  contentHtml?: string | null
  tocJson?: HomeworkTocItem[] | null
  contentHash?: string | null
  status: 'draft' | 'submitted' | 'in_review' | 'completed' | 'returned'
  aiScanStatus: string
  submittedAt?: string | null
  completedAt?: string | null
  completionSource?: string | null
  adminFinalNote?: string | null
  completionNote?: string | null
  studentName?: string
  studentEmail?: string
  classroomName?: string
  commentCount?: number
  pendingAiCount?: number
  homeworkName?: string
  assignmentHtml?: string
  courseId?: number
  caseStudyId?: number | null
  caseStudyName?: string | null
  comments?: HomeworkComment[]
}

export type HomeworkCompletionReportItem = {
  homeworkId: number
  homeworkName: string
  homeworkType: 'submission' | 'self_practice' | string
  notStarted: number
  inProgress: number
  completed: number
  completionRate: number
}

export type ClassroomHomeworkCompletionReport = {
  classroomId: number
  classroomName: string
  courseId?: number
  courseName?: string
  courseCode?: string
  totalStudents: number
  items: HomeworkCompletionReportItem[]
}

export type HomeworkCompletionDetail = {
  homeworkId: number
  homeworkName: string
  homeworkType: string
  classroomId: number
  classroomName: string
  totalStudents: number
  notStarted: number
  inProgress: number
  completed: number
  completionRate: number
  students: Array<{
    userId: number
    name?: string | null
    email?: string | null
    bucket: 'notStarted' | 'inProgress' | 'completed'
    status: string | null
    submissionId?: number | null
    completedAt?: string | null
    completionNote?: string | null
    completionSource?: string | null
  }>
}

export type HomeworkReportClassroom = {
  id: number
  name: string
  code?: string | null
  courseId: number
  courseName?: string
  courseCode?: string
  startDate?: string | null
}

function unwrap<T>(res: any): T {
  if (res?.data?.success === false) throw new Error(res.data.message || 'Lỗi API')
  return (res?.data?.data ?? res?.data) as T
}

export const HomeworkApi = {
  // Admin
  async listByCourse(courseId: number) {
    const res = await apiClient.get(`/admin/courses/${courseId}/homeworks`)
    return unwrap<CourseHomework[]>(res)
  },
  async get(homeworkId: number) {
    const res = await apiClient.get(`/admin/homeworks/${homeworkId}`)
    return unwrap<CourseHomework>(res)
  },
  async create(courseId: number, body: Partial<CourseHomework> & { name: string; contentHtml: string }) {
    const res = await apiClient.post(`/admin/courses/${courseId}/homeworks`, body)
    return unwrap<CourseHomework>(res)
  },
  async update(homeworkId: number, body: Partial<CourseHomework>) {
    const res = await apiClient.put(`/admin/homeworks/${homeworkId}`, body)
    return unwrap<CourseHomework>(res)
  },
  async remove(homeworkId: number) {
    const res = await apiClient.delete(`/admin/homeworks/${homeworkId}`)
    return unwrap<any>(res)
  },
  async assignClassrooms(homeworkId: number, classroomIds: number[], deadline?: string | null) {
    const res = await apiClient.put(`/admin/homeworks/${homeworkId}/classrooms`, {
      classroomIds,
      deadline: deadline || null,
    })
    return unwrap<any>(res)
  },
  async listSkillLinks(homeworkId: number) {
    const res = await apiClient.get(`/admin/homeworks/${homeworkId}/skill-links`)
    return unwrap<any[]>(res)
  },
  async addSkillLinks(
    homeworkId: number,
    body: { nodeIds?: number[]; parentNodeId?: number; expandChildren?: boolean }
  ) {
    const res = await apiClient.post(`/admin/homeworks/${homeworkId}/skill-links`, body)
    return unwrap<any[]>(res)
  },
  async deleteSkillLink(homeworkId: number, linkId: number) {
    await apiClient.delete(`/admin/homeworks/${homeworkId}/skill-links/${linkId}`)
  },
  async bulkDeleteSkillLinks(homeworkId: number, linkIds: number[]) {
    await apiClient.post(`/admin/homeworks/${homeworkId}/skill-links/bulk-delete`, { linkIds })
  },
  async listSubmissions(homeworkId: number, params?: { classroomId?: number; status?: string }) {
    const res = await apiClient.get(`/admin/homeworks/${homeworkId}/submissions`, { params })
    return unwrap<HomeworkSubmission[]>(res)
  },
  async getSubmission(submissionId: number) {
    const res = await apiClient.get(`/admin/homework-submissions/${submissionId}`)
    return unwrap<HomeworkSubmission>(res)
  },
  async addAdminComment(
    submissionId: number,
    body: {
      body: string
      parentId?: number | null
      anchorType?: 'text' | 'image'
      anchorJson?: HomeworkAnchorJson
    }
  ) {
    const res = await apiClient.post(`/admin/homework-submissions/${submissionId}/comments`, body)
    return unwrap<HomeworkComment>(res)
  },
  async approveComment(commentId: number, action: 'approve' | 'reject') {
    await apiClient.patch(`/admin/homework-comments/${commentId}/approval`, { action })
  },
  async resolveCommentAdmin(commentId: number, status: 'open' | 'resolved') {
    await apiClient.patch(`/admin/homework-comments/${commentId}/resolve`, { status })
  },
  async updateSubmissionStatus(
    submissionId: number,
    action: 'complete' | 'return' | 'reopen',
    adminFinalNote?: string | null
  ) {
    const res = await apiClient.patch(`/admin/homework-submissions/${submissionId}/status`, {
      action,
      ...(adminFinalNote !== undefined ? { adminFinalNote } : {}),
    })
    return unwrap<HomeworkSubmission>(res)
  },
  async updateFinalNote(submissionId: number, adminFinalNote: string | null) {
    const res = await apiClient.patch(`/admin/homework-submissions/${submissionId}/final-note`, {
      adminFinalNote,
    })
    return unwrap<HomeworkSubmission>(res)
  },
  async queueAiRescan(submissionId: number) {
    const res = await apiClient.post(`/admin/homework-submissions/${submissionId}/ai-rescan`)
    return unwrap<any>(res)
  },

  async listReportClassrooms() {
    const res = await apiClient.get('/admin/homework-report-classrooms')
    return unwrap<HomeworkReportClassroom[]>(res)
  },
  async getClassroomCompletionReport(classroomId: number) {
    const res = await apiClient.get(`/admin/classrooms/${classroomId}/homework-completion-report`)
    return unwrap<ClassroomHomeworkCompletionReport>(res)
  },
  async getHomeworkCompletionReport(homeworkId: number, classroomId: number) {
    const res = await apiClient.get(`/admin/homeworks/${homeworkId}/completion-report`, {
      params: { classroomId },
    })
    return unwrap<HomeworkCompletionDetail>(res)
  },

  // Student
  async studentList(courseId: number) {
    const res = await apiClient.get(`/student/courses/${courseId}/homeworks`)
    return unwrap<any[]>(res)
  },
  async studentGet(courseId: number, homeworkId: number) {
    const res = await apiClient.get(`/student/courses/${courseId}/homeworks/${homeworkId}`)
    return unwrap<any>(res)
  },
  async studentSaveDraft(courseId: number, homeworkId: number, contentHtml: string) {
    const res = await apiClient.put(`/student/courses/${courseId}/homeworks/${homeworkId}/submission`, {
      contentHtml,
    })
    return unwrap<HomeworkSubmission>(res)
  },
  async studentSubmit(courseId: number, homeworkId: number, contentHtml?: string) {
    const res = await apiClient.post(`/student/courses/${courseId}/homeworks/${homeworkId}/submit`, {
      contentHtml,
    })
    return unwrap<HomeworkSubmission>(res)
  },
  async studentSelfComplete(courseId: number, homeworkId: number, completionNote?: string | null) {
    const res = await apiClient.post(
      `/student/courses/${courseId}/homeworks/${homeworkId}/self-complete`,
      { completionNote: completionNote ?? null }
    )
    return unwrap<HomeworkSubmission>(res)
  },
  async studentComment(submissionId: number, body: string, parentId?: number | null) {
    const res = await apiClient.post(`/student/homework-submissions/${submissionId}/comments`, {
      body,
      parentId: parentId || null,
    })
    return unwrap<HomeworkComment>(res)
  },
  async studentResolve(commentId: number, status: 'open' | 'resolved') {
    await apiClient.patch(`/student/homework-comments/${commentId}/resolve`, { status })
  },
}

export function parseAnchorJson(raw: HomeworkComment['anchorJson']): HomeworkAnchorJson | null {
  if (!raw) return null
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw)
    } catch {
      return null
    }
  }
  return raw
}
